import { ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { SplitTextOnTokensMode, splitTextOnTokens, TokenInfo } from 'utils/string';

export const HighlightedToken = styled.span`
    border-bottom: dashed 1px ${tokens.text.secondary};
`;

export type HighlightedTextProps = Readonly<{
    /**
     * The text to be rendered.
     */
    text: string;
    /**
     * List of tokens to be searched for in the `text`.
     */
    terms: string[];
    /**
     * Mode to use when matching tokens (defaults to `'contains'`).
     */
    mode?: SplitTextOnTokensMode;
    /**
     * Callback that will be called with each section of
     * the `text` to be rendered. If not specified, defaults to dashed underline.
     */
    render?: (info: TokenInfo) => ReactNode;
    /**
     * Optional classes to apply.
     */
    className?: string;
}>;

const defaultRenderer = ({ text, isMatch }: TokenInfo) =>
    isMatch ? <HighlightedToken>{text}</HighlightedToken> : text;

/**
 * Used to conditionally render text based on text matches from the supplied list of tokens.
 *
 * @example
 * <HighlightedText
 *     text="This is a test"
 *     tokens={['is a']}
 *     render={({ text, isMatch }) => (isMatch ? <strong>{text}</strong> : text)}
 * />
 *
 * Result:
 *
 * This <strong>is a</strong> test.
 */
export function HighlightedText({
    text,
    terms,
    render = defaultRenderer,
    className,
    mode = 'contains',
}: HighlightedTextProps) {
    const tokenInfo = useMemo(
        () => (text ? splitTextOnTokens({ text, terms, mode }) : []),
        [text, terms, mode],
    );

    return (
        <span className={className}>
            {tokenInfo.reduce<ReactNode>(
                (result, info) => (
                    <>
                        {result}
                        {render(info)}
                    </>
                ),
                undefined,
            )}
        </span>
    );
}
